import React, { useCallback, useEffect, useState } from 'react'
import { message, Form, Modal, Input, Radio, Button, Table, Select, Typography } from 'antd'
import { getAttributeById, findAttributes, updateAttribute, createAttribute, deleteAttribute } from '@services/attributeService' 
import AttributeValueEdit from './AttributeValueEdit'
import { showError } from '@utils/alert'
import moment from 'moment'
import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import EditableSelectCell from './EditableSelectCell'
import './index.scss'

const { confirm } = Modal
const { Paragraph } = Typography

function AttributeEdit (props: any) {
  const { id, visible, onCancel } = props
  const [form] = Form.useForm()
  const [itemId, setItemId] = useState<string | null>(null)
  const [values, setValues] = useState<any>(null)
  const [valueLoading, setValueLoading] = useState(false)
  const [editValue, setEditValue] = useState<any>(null)
  const [valueModalVisible, setValueModalVisble] = useState(false)

  useEffect(() => {
    setItemId(id)

    return () => {
      setValueModalVisble(false)
      setValues(null)
    }
  }, [id])

  const searchAttrValues = useCallback((id: string | null) => {
    if (!id) return
    setValueLoading(true)
    findAttributes(id, 0, 100)
      .then(r => {
        if (r.data) {
          // console.log(r.data)
          setValues(r.data.items)
        }
        setValueLoading(false)
      })
      .catch(e => {
        setValueLoading(false)
        console.error(e)
      })
  }, [])

  useEffect(() => {
    form.resetFields()
    setValues(null)
    if (!itemId) return
    const hide = message.loading('loading...')
    getAttributeById(itemId!!)
      .then(res => {
        if (res.data) {
          form.setFieldsValue({
            value: res.data.value,
            valid: res.data.valid,
            alias: res.data.alias,
            enabled_search_filter: Boolean(res.data.enabled_search_filter),
          })
          searchAttrValues(res.data.id)
        }
        hide()
      })
      .catch(e => {
        console.error(e)
        hide()
        showError(`get attribute: ${id} failed`, e)
      })
  }, [itemId, form, id, searchAttrValues])
  
  const onEditItem = () => {
    form.validateFields().then(values => {
      const hide = message.loading('更新或者创建中...')
      if (itemId) {
        updateAttribute(itemId, {
          value: values.value,
          valid: values.valid,
          alias: values.alias,
          enabled_search_filter: values.enabled_search_filter,
        })
        .then(res => {
          hide()
          onCancel && onCancel()
          message.success(`update item ${itemId} succeed`)
        })
        .catch(e => {
          hide()
          console.error(e)
          showError('update item failed', e)
        })
      } else {
        createAttribute({
          value: values.value,
          valid: values.valid,
          alias: values.alias,
          enabled_search_filter: values.enabled_search_filter,
          type: 1, // type: item
        })
        .then(res => {
          hide()
          setItemId(res.data.id)
          message.success(`create item ${res.data.id} succeed`)
          onCancel && onCancel()
        })
        .catch(e => {
          hide()
          console.error(e)
          showError('create item failed', e)
        })
      }
    })
    .catch(e => {
      console.error(e)
      showError('from valid failed', e)
    })
  }

  const onEditValue = (v: any) => {
    setValueModalVisble(true)
    setEditValue(v)
  }

  const onDeleteValue = (r: any) => {
    const { id } = r
    confirm({
      title: '删除',
      content: `确认删除属性值: ${id} 吗？`,
      okText: '确认',
      okType: 'danger',
      cancelText: '取消',
      onOk() {
          const hide = message.loading('删除中...')
          deleteAttribute(id)
            .then(res => {
              hide()
              message.success(`删除属性值:${id} 成功`)
              searchAttrValues(itemId)
            })
            .catch(e => {
              hide()
              console.error(e)
              showError(e)
            })
      },
      onCancel() {
        
      },
    })
  }

  const onChangeValue = useCallback((key: string, v: any, id: string) => {
    const hide = message.loading('修改中...')
    updateAttribute(id, {
      [key]: v,
    })
    .then((res) => {
      hide()
      searchAttrValues(itemId)
      message.success('update attr value succeed')
    })
    .catch(e => {
      console.error(e)
      hide()
      showError('update attr value failed', e)
    })
  }, [itemId, searchAttrValues])

  const columns = [
    {
      title: 'Id',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    }, {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      width: 100,
      render: (value, record) => {
        return <Paragraph editable={{ onChange: v => onChangeValue('value', v, record.id) }}>{value}</Paragraph>
      }
    }, {
      title: 'Patterns',
      dataIndex: 'patterns',
      key: 'patterns',
      width: 350,
      render: (records, value) => {
        return records && Array.isArray(records) ? records.map((record, index) => {
          return (
            <>
              {
                Array.isArray(record.terms) ? (
                  <EditableSelectCell
                    label="terms: "
                    value={record.terms}
                    onChange={(update) => {
                      records[index] = { negativeTerms: record?.negativeTerms, terms: update }
                      onChangeValue('patterns', records, value.id)
                    }}
                  />
                ) : null
              }
              {
                Array.isArray(record.negativeTerms) ? (
                  <EditableSelectCell
                    label="neg. terms: "
                    value={record.negativeTerms}
                    onChange={(update) => {
                      records[index] = { negativeTerms: update, terms: record?.terms }
                      onChangeValue('patterns', records, value.id)
                    }}
                  />
                ) : null
              }
              { index !== records.length - 1 ? (
                <div style={{ width: '100%', borderBottom: '1px solid #f0f0f0', margin: '2px 0' }} />
              ) : null }
            </>
          )
        }) : null
      }
    }, {
      title: 'Status',
      dataIndex: 'valid',
      width: 80,
      render: (valid) => {
        return valid ? '启用' : '禁用'
      }
    }, {
      title: 'Modified',
      dataIndex: 'last_modified_at',
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.last_modified_at - b.last_modified_at,
      width: 145,
      render: (record) => {
        return moment(record).format('YYYY-MM-DD HH:mm')
      }
    }, {
      title: 'Created At',
      dataIndex: 'created_at',
      width: 100,
      render: (record) => {
        return moment(record).format('YYYY-MM-DD')
      }
    }, {
      title: 'Action',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (record) => (
        <div style={{ display: 'flex' }}>
          <Button
            size="small"
            style={{ marginRight: 8 }}
            type="default"
            onClick={() => onEditValue(record)}
            shape="circle"
            icon={<EditOutlined />} />
          <Button
            size="small"
            danger
            onClick={() => onDeleteValue(record)}
            shape="circle"
            icon={<DeleteOutlined />} />
        </div>
      )
    }
  ]

  return (
    <Modal
      title={id ? '更新' : '新建'}
      centered
      width={800}
      destroyOnClose
      closable
      visible={visible}
      onCancel={onCancel}
      maskClosable={false}
      footer={
        <>
          <Button onClick={onCancel} type="default">取消</Button>
          <Button onClick={onEditItem} type="primary">保存</Button>
        </>
      }
    >
      <Form layout="inline" preserve={false} name="attr-item" form={form}>
        <Form.Item name="value" label="Name" required>
          <Input />
        </Form.Item>
        <Form.Item name="alias" label="Alias">
          <Select style={{ width: '150px' }} mode="tags" placeholder="alias" />
        </Form.Item>
        <Form.Item name="valid" label="Valid" initialValue={true} required>
          <Radio.Group>
            <Radio value={true}>启用</Radio>
            <Radio value={false}>禁用</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item name="enabled_search_filter" label="Search Filter" initialValue={false} required>
          <Radio.Group>
            <Radio value={true}>启用</Radio>
            <Radio value={false}>禁用</Radio>
          </Radio.Group>
        </Form.Item>
      </Form>
      {itemId ? (
        <Table
          rowKey={(record: any) => record.id}
          className="attr-table"
          style={{ height: 380 }}
          title={() => (
            <div style={{ display: 'flex' }}>
              <div style={{ flex: 1 }}><strong>属性值列表</strong></div>
              <Button
                onClick={() => {
                  if (!itemId) {
                    message.error('请先保存属性项, 生成属性项 id')
                    return
                  }
                  setEditValue(null)
                  setValueModalVisble(true)
                }}
                type="primary"
              >
                新建
              </Button>
            </div>
          )}
          size="small"
          bordered
          columns={columns as any}
          dataSource={values}
          loading={valueLoading}
          pagination={false}
          scroll={{ x: 300, y: 300 }}
        />
      ) : null}

      <AttributeValueEdit
        visible={valueModalVisible}
        id={itemId}
        value={editValue}
        onCancel={() => {
          setEditValue(null)
          setValueModalVisble(false)
          searchAttrValues(itemId)
        }} 
      />
    </Modal>
  )
}

export default AttributeEdit
